'use client';

import {
  Avatar,
  AvatarStack,
  type AvatarStackItem,
  Button,
  Icon,
  Menu,
  MenuContent,
  MenuItem,
  MenuSearch,
  MenuSection,
  MenuTrigger,
} from '@lulalife/design-system';
import {
  Copy01,
  DotsHorizontal,
  MessageTextSquare02,
  Plus,
} from '@lulalife/design-system/icons/line';
import { type ReactNode, useState } from 'react';
import type { AssigneeDetails } from 'src/helper/shared-types';
import { createAbbreviation } from 'src/helper/utils';
import { MenuScrollRegion } from './MenuScrollRegion';

/**
 * Figma 7431:38466's columns (100, 204, 110, 170, 110, 160, 100, 120, 104 of 1178). Address has no
 * width: it takes what's left (the bordered box is 2 narrower than the frame's table), and grows
 * when Manage Columns hides a column.
 */
export const SERVICE_REQUEST_LIST_COLUMN_WIDTHS = {
  id: 'w-[100px]',
  market: 'w-[110px]',
  type: 'w-[170px]',
  assignee: 'w-[110px]',
  status: 'w-[160px]',
  source: 'w-[100px]',
  added: 'w-[120px]',
  actions: 'w-[104px]',
} as const;

/**
 * Each column's floor (px): the fixed columns' widths, and Address at 197, which is what the frame's
 * 204 comes to inside the list box at the 1456 design width (its two 1px borders and its 5px
 * scrollbar sit inside Figma's 1178). Past the sum the table scrolls sideways in its own box, so
 * Address no longer shrinks to nothing at narrower widths (21px at 1280).
 */
export const SERVICE_REQUEST_LIST_COLUMN_MIN_WIDTHS = {
  id: 100,
  address: 197,
  market: 110,
  type: 170,
  assignee: 110,
  status: 160,
  source: 100,
  added: 120,
  actions: 104,
} as const;

/** Assignee Picker · Open (7627:27100) and Row Menu · Open (7627:26863). */
const ASSIGNEE_PICKER_WIDTH = 260;
const ROW_MENU_WIDTH = 220;

/** A cell's text on one line, cut with an ellipsis; the full text in its tooltip. */
export const ListTextCell = ({ children }: { children: string }) => (
  <span className="block truncate" title={children}>
    {children}
  </span>
);

/**
 * The row's own control (the ID): opens what a click on the row opens. The row click is mouse-only,
 * so this is the keyboard and screen-reader path.
 */
export const ListRowOpenButton = ({
  label,
  onClick,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: {
  label: string;
  onClick: () => void;
  'aria-label': string;
  'data-testid'?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={ariaLabel}
    data-testid={testId}
    className="block max-w-full cursor-pointer truncate rounded-sm border-0 bg-transparent p-0 text-left desktop-body-md-base text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
  >
    {label}
  </button>
);

const avatarItem = (assignee: AssigneeDetails): AvatarStackItem =>
  assignee.profile_pic
    ? { type: 'photo', src: assignee.profile_pic, alt: '' }
    : { type: 'initial', initials: createAbbreviation(assignee.name) };

/**
 * DS-GAP(avatar-add-trigger): DS `Avatar` Type=Add is always its own `<button>`, with no accessible
 * name and no ref, so it can't sit inside the picker's trigger or be one. This is its markup (36,
 * dashed `border/brand`, `bg/brand-subtle`, plus) as a plain span inside our trigger button.
 */
const AddAvatar = ({ 'data-testid': testId }: { 'data-testid'?: string }) => (
  <span
    className="relative inline-flex size-size-avatar-sm shrink-0 rounded-avatar-radius"
    data-testid={testId}
  >
    <span className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-avatar-radius border border-dashed border-border-brand bg-bg-brand-subtle text-text-link">
      <Plus className="size-size-icon-sm shrink-0" strokeWidth={1.5} aria-hidden="true" />
    </span>
  </span>
);

/**
 * Avatar Small for each assignee, or the Add avatar when there is none. Several assignees overlap
 * as a DS AvatarStack; at most two circles fit the 110 column, so three or more show the first
 * and "+N".
 */
export const AssigneeAvatars = ({
  assignees,
  addTestId,
}: {
  assignees: ReadonlyArray<AssigneeDetails>;
  /** `data-testid` of the Add avatar. */
  addTestId?: string;
}) => {
  if (assignees.length === 0) return <AddAvatar data-testid={addTestId} />;
  if (assignees.length === 1) {
    const [only] = assignees;
    return only.profile_pic ? (
      <Avatar type="photo" size="sm" src={only.profile_pic} alt="" />
    ) : (
      <Avatar type="initial" size="sm" initials={createAbbreviation(only.name)} />
    );
  }
  return (
    <AvatarStack size="sm" max={assignees.length > 2 ? 1 : 2} avatars={assignees.map(avatarItem)} />
  );
};

/**
 * DS-GAP(menu-item-avatar-initials): `MenuItem` Type=Avatar only draws a photo. A user without one
 * gets the DS initials Avatar as the item's icon, and the icon box grows from 20 to the avatar's 24
 * so the names line up with the photo rows.
 */
const AVATAR_ICON_ITEM_CLASS_NAME = '[&_.size-size-icon-md:has(>.avatar)]:size-size-avatar-xs';

/** The lookup can send a user with no name (null, despite the type); they read "Unnamed user". */
const displayName = (user: AssigneeDetails) => user.name?.trim() || 'Unnamed user';

const UserMenuItem = ({
  user,
  selected,
  onClick,
}: {
  user: AssigneeDetails;
  selected: boolean;
  onClick: () => void;
}) =>
  user.profile_pic ? (
    <MenuItem
      type="avatar"
      label={displayName(user)}
      avatarSrc={user.profile_pic}
      rightX={selected}
      onClick={onClick}
    />
  ) : (
    <MenuItem
      label={displayName(user)}
      leftIcon={
        <Avatar type="initial" size="xs" initials={createAbbreviation(displayName(user))} />
      }
      rightX={selected}
      onClick={onClick}
      className={AVATAR_ICON_ITEM_CLASS_NAME}
    />
  );

const matches = (user: AssigneeDetails, query: string) =>
  displayName(user).toLowerCase().includes(query.trim().toLowerCase());

/**
 * The Assignee(s) cell and its picker (7627:27100): "Search...", the assigned users under
 * "Assignee(s)" (x removes), then everyone else under "Users" (click adds).
 */
export const AssigneePickerMenu = ({
  assignees,
  users,
  onAdd,
  onRemove,
  testIds,
}: {
  assignees: ReadonlyArray<AssigneeDetails>;
  /** Everyone who can be assigned. */
  users: ReadonlyArray<AssigneeDetails>;
  onAdd: (user: AssigneeDetails) => void;
  onRemove: (user: AssigneeDetails) => void;
  testIds?: { trigger: string; menu: string; add: string };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const assignedIds = new Set(assignees.map((user) => user.user_id));
  const selected = assignees.filter((user) => matches(user, query));
  const others = users.filter((user) => !assignedIds.has(user.user_id) && matches(user, query));
  const names = assignees.map(displayName).join(', ');

  return (
    <Menu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setQuery('');
      }}
    >
      <MenuTrigger>
        <button
          type="button"
          aria-label={names ? `Assignees: ${names}` : 'Add assignee'}
          data-testid={testIds?.trigger}
          className="inline-flex cursor-pointer rounded-avatar-radius border-0 bg-transparent p-0 align-middle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
        >
          <AssigneeAvatars assignees={assignees} addTestId={testIds?.add} />
        </button>
      </MenuTrigger>
      <MenuContent
        data-lula-ds=""
        width={ASSIGNEE_PICKER_WIDTH}
        aria-label="Assignees"
        data-testid={testIds?.menu}
        // 7627:27100 is 284 tall: the search stays put and the users scroll under it.
        // DS-GAP(menu-scroll-region): MenuContent has no max height or scrolling body, so the
        // panel is capped here and the sections sit in their own scroller, which also stops above
        // the window's bottom.
        className="max-h-[284px]"
      >
        <MenuSearch value={query} onChange={setQuery} placeholder="Search..." />
        <MenuScrollRegion className="flex-1">
          {selected.length > 0 && (
            <MenuSection label="Assignee(s)">
              {selected.map((user) => (
                <UserMenuItem
                  key={user.user_id}
                  user={user}
                  selected
                  onClick={() => onRemove(user)}
                />
              ))}
            </MenuSection>
          )}
          {others.length > 0 && (
            <MenuSection label="Users">
              {others.map((user) => (
                <UserMenuItem
                  key={user.user_id}
                  user={user}
                  selected={false}
                  onClick={() => onAdd(user)}
                />
              ))}
            </MenuSection>
          )}
          {selected.length === 0 && others.length === 0 && (
            <MenuItem label="No Users Found" disabled />
          )}
        </MenuScrollRegion>
      </MenuContent>
    </Menu>
  );
};

/** A copy action in the row menu. `id` keys it (its label changes to "… Copied!") and is its `data-testid`. */
type ListRowMenuItem = { id: string; label: string; onClick: () => void };

/** A tertiary icon button (36, glyph 16), as Figma's Button Icon. */
const RowIconButton = ({
  icon,
  ...props
}: Omit<Parameters<typeof Button>[0], 'children' | 'variant' | 'iconOnly'> & {
  icon: ReactNode;
}) => (
  <Button type="button" variant="tertiary" iconOnly {...props}>
    <Icon size="sm" aria-hidden="true">
      {icon}
    </Icon>
  </Button>
);

type ListRowActionsProps = {
  /** Left out when the user can't open the chat (and on lists with no message button). */
  message?: { unreadCount: number; onClick: () => void; 'data-testid'?: string };
  moreTestId?: string;
} & (
  | { menuItems: ReadonlyArray<ListRowMenuItem>; renderMenu?: never; menuWidth?: never }
  | {
      menuItems?: never;
      /** The row menu's own items (Work Orders 8234:17668), mounted only while it is open. */
      renderMenu: (close: () => void) => ReactNode;
      menuWidth: number;
    }
);

/**
 * Cell Content Type=Actions (Service Requests 7431:38466, Work Orders 7453:980): Message with its
 * unread Badge Count when given, then More with the row menu (copy items as 7627:26863, or the
 * caller's own items). Right-aligned, 8 apart.
 */
export const ListRowActions = ({
  message,
  menuItems,
  renderMenu,
  menuWidth,
  moreTestId,
}: ListRowActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="flex items-center justify-end gap-space-8">
      {message && (
        <RowIconButton
          icon={<MessageTextSquare02 width="100%" height="100%" strokeWidth={1.5} />}
          aria-label={
            message.unreadCount > 0
              ? `Messages: ${message.unreadCount} unread`
              : 'Messages: no unread messages'
          }
          showBadge={message.unreadCount > 0}
          badgeText={message.unreadCount}
          // DS-GAP(button-badge-position): the DS Button pins its badge at top -4 / right -4, so a
          // "999+" grows leftwards. Figma's Badge Count sits at left 22 / top 0 of the 36 button and
          // grows to the right.
          className="[&>div.absolute]:top-0 [&>div.absolute]:right-auto [&>div.absolute]:left-[22px]"
          onClick={message.onClick}
          data-testid={message['data-testid']}
        />
      )}
      <Menu open={isOpen} onOpenChange={setIsOpen}>
        <MenuTrigger>
          <RowIconButton
            icon={<DotsHorizontal width="100%" height="100%" strokeWidth={1.5} />}
            aria-label="More actions"
            data-testid={moreTestId}
          />
        </MenuTrigger>
        <MenuContent
          data-lula-ds=""
          align="end"
          width={menuWidth ?? ROW_MENU_WIDTH}
          aria-label="Row actions"
        >
          <MenuScrollRegion>
            {renderMenu
              ? renderMenu(() => setIsOpen(false))
              : menuItems.map((item) => (
                  <div key={item.id} data-testid={item.id}>
                    <MenuItem
                      label={item.label}
                      leftIcon={<Copy01 width="100%" height="100%" strokeWidth={1.5} />}
                      onClick={item.onClick}
                    />
                  </div>
                ))}
          </MenuScrollRegion>
        </MenuContent>
      </Menu>
    </div>
  );
};

/**
 * The More button as Work Orders' Loading frame (8012:127198) draws it in every placeholder row:
 * the real button, inert, since the row behind it isn't there yet.
 */
export const ListRowMorePlaceholder = () => (
  <div className="flex items-center justify-end">
    <RowIconButton
      icon={<DotsHorizontal width="100%" height="100%" strokeWidth={1.5} />}
      aria-label="More actions"
      tabIndex={-1}
      aria-hidden="true"
    />
  </div>
);
